/* ============================================================================
 *  CONFIG ASSURANCE MALADIE — Barème & calcul de prime
 * ----------------------------------------------------------------------------
 *  ⚠️  IMPORTANT : les `taux` ci-dessous sont des VALEURS EXEMPLE.
 *      Remplace-les par ta vraie grille actuarielle.
 *      Le souscripteur NE VOIT JAMAIS ces taux : ils ne servent qu'au calcul
 *      de la prime en arrière-plan (prime par personne + prime totale).
 * ==========================================================================*/

/* --- Liste des affections proposées au questionnaire (Q1) ---------------- */
/* taux = majoration multiplicative appliquée à la prime de base de l'âge.   */
/* Ex : 0.15 = +15 %.                                                        */
export const AFFLICTIONS = [
  { code: "HTA", label: "Hypertension artérielle", taux: 0.15 },
  { code: "DIABETE", label: "Diabète", taux: 0.2 },
  { code: "ASTHME", label: "Asthme", taux: 0.1 },
  { code: "DREPANOCYTOSE", label: "Drépanocytose", taux: 0.25 },
  { code: "INSUF_RENALE", label: "Insuffisance rénale", taux: 0.3 },
  { code: "CARDIOPATHIE", label: "Maladie cardiaque", taux: 0.25 },
  { code: "VIH", label: "VIH / SIDA", taux: 0.3 },
  { code: "CANCER", label: "Cancer", taux: 0.35 },
  { code: "ULCERE", label: "Ulcère gastro-duodénal", taux: 0.08 },
  { code: "EPILEPSIE", label: "Épilepsie", taux: 0.15 },
  { code: "HEPATITE", label: "Hépatite B / C", taux: 0.18 },
  { code: "THYROIDE", label: "Troubles thyroïdiens", taux: 0.1 },
  { code: "RHUMATISME", label: "Rhumatisme / Arthrose", taux: 0.08 },
  { code: "ASTHENIE", label: "Anémie chronique", taux: 0.12 },
  { code: "AUTRE", label: "Autre affection", taux: 0.1 },
];

/* Accès rapide par code */
export const AFFLICTION_MAP = AFFLICTIONS.reduce((acc, a) => {
  acc[a.code] = a;
  return acc;
}, {});

/* --- Grille de prime de base par tranche d'âge (FCFA) -------------------- */
/*  Bornes INCLUSIVES. > 70 ans => non éligible (prime = null).             */
export const PRIME_BASE_BY_AGE = [
  { min: 0, max: 20, prime: 107000 },
  { min: 21, max: 40, prime: 157000 },
  { min: 41, max: 60, prime: 232000 },
  { min: 61, max: 65, prime: 462000 },
  { min: 66, max: 70, prime: 462000 },
];

export const AGE_MAX_ELIGIBLE = 70;

/* Suggestions de médicaments (Q2) — purement cosmétique, cliquables.        */
/* Le champ reste libre : ce ne sont que des raccourcis de saisie.           */
export const MEDICATION_SUGGESTIONS = [
  "Paracétamol",
  "Metformine",
  "Amlodipine",
  "Insuline",
  "Salbutamol",
  "Aténolol",
  "Oméprazole",
  "Acide folique",
];

/* ============================================================================
 *  HELPERS DE CALCUL (pures, testables)
 * ==========================================================================*/

/** Âge en années révolues à partir d'une date ISO (YYYY-MM-DD). */
export function computeAge(birthDate) {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}

/** Prime de base selon l'âge. Renvoie null si hors grille (> 70 ans). */
export function getBaseByAge(age) {
  if (age === null || age === undefined || isNaN(age)) return null;
  const tranche = PRIME_BASE_BY_AGE.find((t) => age >= t.min && age <= t.max);
  return tranche ? tranche.prime : null;
}

/** Somme des taux des affections sélectionnées (par codes). */
export function sumAfflictionRate(codes = []) {
  return codes.reduce((sum, code) => {
    const a = AFFLICTION_MAP[code];
    return a ? sum + a.taux : sum;
  }, 0);
}

/**
 * Prime individuelle = base_âge × (1 + Σ taux).
 * Renvoie { prime, base, age, rate, eligible }.
 * prime = null si non éligible (âge hors grille ou date manquante).
 */
export function computeIndividualPrime(person) {
  const age = computeAge(person && person.birthDate);
  const base = getBaseByAge(age);
  if (base === null) {
    return { prime: null, base: null, age, rate: 0, eligible: false };
  }
  const rate = sumAfflictionRate(person.afflictionCodes || []);
  const prime = Math.round(base * (1 + rate));
  return { prime, base, age, rate, eligible: true };
}

/** Formatage FCFA. */
export function formatFCFA(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " FCFA";
}

/** Libellé lisible d'un affilié. */
export function affiliationTypeLabel(type) {
  return { conjoint: "Conjoint(e)", enfant: "Enfant" }[type] || "Affilié";
}
